import type { Grading } from "@memorang/types";

export type OrderingAnswerKey = Extract<
	Grading,
	{ type: "ANSWER_KEY_ORDERING" }
>["answerKey"];

export const deriveCorrectOrder = (
	answerKey: OrderingAnswerKey | undefined,
): string[] | undefined => {
	if (!Array.isArray(answerKey) || answerKey.length === 0) {
		return undefined;
	}
	const successors = new Map<string, string>();
	const segmentIds = new Set<string>();
	for (const entry of answerKey) {
		if (entry.segmentIds.length !== 2) {
			return undefined;
		}
		const [from, to] = entry.segmentIds.map(String);
		if (successors.has(from)) {
			return undefined;
		}
		successors.set(from, to);
		segmentIds.add(from);
		segmentIds.add(to);
	}
	const followers = new Set(successors.values());
	const heads = [...successors.keys()].filter((id) => !followers.has(id));
	if (heads.length !== 1) {
		return undefined;
	}
	const order: string[] = [];
	let current: string | undefined = heads[0];
	while (current !== undefined) {
		if (order.includes(current)) {
			return undefined;
		}
		order.push(current);
		current = successors.get(current);
	}
	return order.length === segmentIds.size ? order : undefined;
};

export const resolveCorrectOrder = (
	answerKey: OrderingAnswerKey | undefined,
	correctOrder?: (number | string)[],
): string[] | undefined => {
	if (Array.isArray(correctOrder) && correctOrder.length > 0) {
		return correctOrder.map(String);
	}
	return deriveCorrectOrder(answerKey);
};

export const getOrderingMaxPoints = (
	answerKey: OrderingAnswerKey | undefined,
): number =>
	Array.isArray(answerKey)
		? answerKey.reduce((sum, entry) => sum + (entry.points ?? 0), 0)
		: 0;

export const getPointsPerPair = (
	correctOrder: string[] | undefined,
	maxPoints: number | undefined,
) =>
	correctOrder && correctOrder.length > 1 && maxPoints !== undefined
		? maxPoints / (correctOrder.length - 1)
		: 1;

export const getPairCredits = (
	orderedIds: string[],
	placedCount: number,
	correctOrder?: string[],
): (boolean | undefined)[] => {
	const positions = correctOrder
		? new Map(correctOrder.map((id, index) => [id, index]))
		: null;
	const credits: (boolean | undefined)[] = [];
	for (let i = 0; i < orderedIds.length - 1; i++) {
		if (!positions || i + 1 >= placedCount) {
			credits.push(undefined);
			continue;
		}
		const fromIndex = positions.get(orderedIds[i]);
		const toIndex = positions.get(orderedIds[i + 1]);
		credits.push(
			fromIndex !== undefined && toIndex !== undefined && toIndex > fromIndex,
		);
	}
	return credits;
};

export const getOrderingEarnedPoints = (
	answerKey: OrderingAnswerKey | undefined,
	selectedOrder: string[] | undefined,
	explicitCorrectOrder?: (number | string)[],
): number => {
	if (!selectedOrder || selectedOrder.length < 2) {
		return 0;
	}
	const correctOrder = resolveCorrectOrder(answerKey, explicitCorrectOrder);
	if (!correctOrder || correctOrder.length < 2) {
		return 0;
	}
	const pointsPerPair =
		getOrderingMaxPoints(answerKey) / (correctOrder.length - 1);
	const creditedPairs = getPairCredits(
		selectedOrder,
		selectedOrder.length,
		correctOrder,
	).filter((credit) => credit === true).length;
	return Math.round(creditedPairs * pointsPerPair * 100) / 100;
};
